/* @HEADER
 * @version 1.1.1375 | 2026-10-31
 * @purpose ZVX-5305 — the sentence of the banner «you were removed from an organisation»: one organisation, or a list of every one the person was removed from, with the unnamed ones counted.
 * @sidecar leftOrganisationsSentence.ts.header.md
 * @layer app | @hex adapter | @ctx project
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-5305, ZVX-DEV-8269

/** The words the sentence is made of, from the messages layer. */
export interface LeftOrganisationWords {
  title: (organisation: string) => string
  titleUnnamed: string
  body: string
  titleSeveral: (list: string) => string
  titleSeveralUnnamed: string
  bodySeveral: string
  /** «A и B» in the person's language: the word between the last two items. */
  and: string
  /** «ещё N» / «N more». */
  more: (count: number) => string
  /** The quotes around one organisation's name. */
  quote: (name: string) => string
}

export interface LeftOrganisationsNotice {
  organisationName: string | null
  organisationNames?: readonly string[]
  unnamedCount?: number
}

/**
 * ZVX-5305 -- one organisation reads as before; several are named in ONE sentence, the unnamed ones as a trailing «and N more».
 * A notice made before the list existed has only `organisationName`, which is then the whole list.
 */
export function leftOrganisationsSentence(words: LeftOrganisationWords, notice: LeftOrganisationsNotice): { title: string; body: string } {
  const names = notice.organisationNames ?? (notice.organisationName ? [notice.organisationName] : [])
  const unnamed = Math.max(0, Math.floor(notice.unnamedCount ?? 0))
  const total = names.length + unnamed

  if (total <= 1) {
    return { title: names[0] ? words.title(names[0]) : words.titleUnnamed, body: words.body }
  }
  if (names.length === 0) return { title: words.titleSeveralUnnamed, body: words.bodySeveral }

  const parts = names.map(words.quote)
  if (unnamed > 0) parts.push(words.more(unnamed))
  const list = parts.length === 2 ? `${parts[0]} ${words.and} ${parts[1]}` : `${parts.slice(0, -1).join(', ')} ${words.and} ${parts[parts.length - 1]}`
  return { title: words.titleSeveral(list), body: words.bodySeveral }
}
